import { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  usePaymentAccount,
  useCreatePaymentAccount,
  useUpdatePaymentAccount,
} from '@/lib/hooks/usePaymentAccounts';
import type { PaymentAccountInput, PaymentAccountUpdateInput } from '@cms/models';
import { STATUS_LOWER, STATUS_OPTIONS } from '@cms/utils';
import { withAreaPrefix } from '@/lib/area-routes';
import { NativeButtonWithIcon } from '@/components/common/NativeButtonWithIcon';

const TYPE_OPTIONS = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CRYPTO', label: 'Crypto' },
] as const;

const MAX_QR_BYTES = 500 * 1024; // 500KB
const VALID_QR_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PaymentAccountFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toAreaPath = (path: string): string => withAreaPrefix(path, location.pathname);
  const isEdit = id !== undefined && id !== 'new';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: account, isLoading: accountLoading } = usePaymentAccount(isEdit && id ? id : '');
  const createMutation = useCreatePaymentAccount();
  const updateMutation = useUpdatePaymentAccount();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm<Omit<PaymentAccountInput, 'status'> & { status?: 'active' | 'inactive' }>({
    defaultValues: {
      type: 'BANK_TRANSFER',
      name: '',
      status: 'active',
      sortOrder: 0,
      bankName: undefined,
      accountNumber: undefined,
      currency: undefined,
      qrCodeBase64: undefined,
      walletAddress: undefined,
      network: undefined,
    },
  });

  const typeValue = watch('type');

  useEffect(() => {
    if (account) {
      reset({
        type: account.type as 'BANK_TRANSFER' | 'CRYPTO',
        name: account.name,
        status:
          account.status?.toLowerCase() === STATUS_LOWER.INACTIVE
            ? STATUS_LOWER.INACTIVE
            : STATUS_LOWER.ACTIVE,
        sortOrder: account.sortOrder ?? 0,
        bankName: account.bankName ?? undefined,
        accountNumber: account.accountNumber ?? undefined,
        currency: account.currency ?? undefined,
        qrCodeBase64: account.qrCodeBase64 ?? undefined,
        walletAddress: account.walletAddress ?? undefined,
        network: account.network ?? undefined,
      });
    }
  }, [account, reset]);

  const handleQrUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      clearErrors('qrCodeBase64');
      if (!VALID_QR_TYPES.includes(file.type)) {
        setError('qrCodeBase64', { message: 'QR must be PNG or JPEG' });
        return;
      }
      if (file.size > MAX_QR_BYTES) {
        setError('qrCodeBase64', { message: 'QR image must not exceed 500KB' });
        return;
      }
      try {
        const base64 = await fileToBase64(file);
        setValue('qrCodeBase64', base64);
      } catch {
        setError('qrCodeBase64', { message: 'Failed to read image' });
      }
      e.target.value = '';
    },
    [setValue, setError, clearErrors]
  );

  const removeQr = useCallback(() => {
    setValue('qrCodeBase64', undefined);
    clearErrors('qrCodeBase64');
  }, [setValue, clearErrors]);

  const onSubmit = async (
    data: Omit<PaymentAccountInput, 'status'> & { status?: 'active' | 'inactive' }
  ) => {
    try {
      const payload = {
        ...data,
        status: (data.status ?? 'active').toLowerCase(),
      } as PaymentAccountInput;
      if (isEdit && id) {
        await updateMutation.mutateAsync({
          id,
          data: payload as PaymentAccountUpdateInput,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }
      navigate(toAreaPath('/settings/payment-accounts'));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || 'Failed to save payment account');
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-2">
      <div className="flex items-center justify-between">
        {accountLoading && isEdit ? (
          <div className="h-9 flex items-center text-muted-foreground">
            Loading payment account...
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold">
              {isEdit ? 'Edit Payment Account' : 'Create Payment Account'}
            </h1>
            <NativeButtonWithIcon
              type="button"
              onClick={() => navigate(toAreaPath('/settings/payment-accounts'))}
              icon="x"
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
            >
              Cancel
            </NativeButtonWithIcon>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="glass-card p-4 space-y-4">
        {accountLoading && isEdit ? (
          <div className="flex justify-center items-center h-64">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="animate-spin text-primary"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                className="w-full px-3 py-2 border border-border rounded-md bg-background"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Type *</label>
              <select
                {...register('type', { required: 'Type is required' })}
                className="w-full px-3 py-2 border border-border rounded-md bg-background"
              >
                {TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-destructive">{errors.type.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Status *</label>
              <select
                {...register('status', { required: 'Status is required' })}
                className="w-full px-3 py-2 border border-border rounded-md bg-background"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Sort order</label>
              <input
                {...register('sortOrder', { valueAsNumber: true })}
                type="number"
                min={0}
                className="w-full px-3 py-2 border border-border rounded-md bg-background"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Currency</label>
              <input
                {...register('currency')}
                type="text"
                placeholder="e.g. VND, USD"
                className="w-full px-3 py-2 border border-border rounded-md bg-background"
              />
            </div>

            {typeValue === 'BANK_TRANSFER' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Bank name *
                  </label>
                  <input
                    {...register('bankName')}
                    type="text"
                    placeholder="e.g. Vietcombank"
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                  />
                  {errors.bankName && (
                    <p className="mt-1 text-sm text-destructive">{errors.bankName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Account number *
                  </label>
                  <input
                    {...register('accountNumber')}
                    type="text"
                    placeholder="e.g. 1234567890"
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                  />
                  {errors.accountNumber && (
                    <p className="mt-1 text-sm text-destructive">{errors.accountNumber.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    QR Code (PNG/JPEG, max 500KB)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleQrUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2">
                    <NativeButtonWithIcon
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      icon="upload"
                      className="px-3 py-2 border border-border rounded-md hover:bg-muted text-sm"
                    >
                      Upload QR
                    </NativeButtonWithIcon>
                    {watch('qrCodeBase64') && (
                      <>
                        <img
                          src={watch('qrCodeBase64') ?? ''}
                          alt="QR preview"
                          className="h-16 w-16 object-contain border border-border rounded"
                        />
                        <NativeButtonWithIcon
                          type="button"
                          onClick={removeQr}
                          icon="x"
                          className="text-sm text-destructive hover:underline"
                        >
                          Remove
                        </NativeButtonWithIcon>
                      </>
                    )}
                  </div>
                  {errors.qrCodeBase64 && (
                    <p className="mt-1 text-sm text-destructive">{errors.qrCodeBase64.message}</p>
                  )}
                </div>
              </>
            )}

            {typeValue === 'CRYPTO' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Wallet address *
                  </label>
                  <input
                    {...register('walletAddress')}
                    type="text"
                    placeholder="e.g. T..."
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                  />
                  {errors.walletAddress && (
                    <p className="mt-1 text-sm text-destructive">{errors.walletAddress.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Network *
                  </label>
                  <input
                    {...register('network')}
                    type="text"
                    placeholder="e.g. TRC20, ERC20"
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                  />
                  {errors.network && (
                    <p className="mt-1 text-sm text-destructive">{errors.network.message}</p>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end gap-4 pt-4">
              <NativeButtonWithIcon
                type="button"
                onClick={() => navigate(toAreaPath('/settings/payment-accounts'))}
                icon="x"
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
              >
                Cancel
              </NativeButtonWithIcon>
              <NativeButtonWithIcon
                type="submit"
                icon={isEdit ? 'circle-check' : 'plus'}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                {isEdit ? 'Update' : 'Create'}
              </NativeButtonWithIcon>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
